import type { WorkfrontMappingKind } from '@atlas/types'
import { useQueryClient } from '@tanstack/react-query'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
	type DraftRowSave,
	MAPPINGS_KEY,
	useQuickPublish,
	useRemoveDraftRow,
	useSaveDraftRows,
} from '@/hooks/useWorkfrontMappings'
import { apiErrorMessage, apiErrorStatus } from '@/lib/api-error'
import { notification } from '@/notifications/notification-store'
import type { MappingRow } from '@/services/workfront-mappings.service'
import type { DrawerContext } from './components/drawer-context'
import { MappingDrawer } from './components/MappingDrawer'
import { type FieldErrors, fieldErrorOf } from './validation'

/** The key a new row is saved under: the server keys it from its content. */
export const NEW_ROW_KEY = 'new'

/** The row open in the drawer, and how it was opened. */
export interface EditState {
	/** The key the row is listed under, or {@link NEW_ROW_KEY}. */
	key: string
	row: MappingRow
	title: string
	/** Several value labels mapped to one value: their keys and labels, in step. */
	several?: { key: string; label: string }[]
	sourceLabel?: string | null
	/** Opened from an error's Fix: the drawer offers Publish now. */
	fromFix?: boolean
	canDelete?: boolean
}

const STALE_DRAFT =
	'The draft had changed since it was loaded: the row is reloaded with the other changes. Check it and save again.'

/**
 * The edit drawer of one mapping kind and everything a save can end in: "Draft
 * saved", a refusal's message under its field and in the status bar, or a stale
 * revision, which reloads the row and says so.
 */
export function useMappingEditor(kind: WorkfrontMappingKind, resolve: (key: string) => MappingRow | undefined) {
	const qc = useQueryClient()
	const save = useSaveDraftRows()
	const quick = useQuickPublish()
	const remove = useRemoveDraftRow()
	const [editing, setEditing] = useState<EditState | null>(null)
	const [errors, setErrors] = useState<FieldErrors>({})
	const [reloads, setReloads] = useState(0)

	const open = (state: EditState) => {
		setErrors({})
		setEditing(state)
	}
	const close = () => {
		setEditing(null)
		setErrors({})
	}

	const refused = (cause: unknown, what: string) => {
		if (apiErrorStatus(cause) === 409) {
			notification.warning(STALE_DRAFT)
			void qc.refetchQueries({ queryKey: MAPPINGS_KEY }).then(() => setReloads((n) => n + 1))
			return
		}
		const message = apiErrorMessage(cause, `Could not ${what}.`)
		const fieldErrors = fieldErrorOf(message)
		setErrors(Object.keys(fieldErrors).length ? fieldErrors : { _: message })
		// The field's name is for the form; the status bar reads the message alone.
		notification.error(`Could not ${what}: ${Object.values(fieldErrors)[0] ?? message}`)
	}

	/** Saves rows into the draft without the drawer: the bulk actions. */
	const saveMany = (saves: DraftRowSave[], done: string, after?: () => void) =>
		save.mutate(
			{ kind, saves },
			{
				onSuccess: () => {
					notification.success(done)
					after?.()
				},
				onError: (cause) => refused(cause, saves.length > 1 ? 'save the rows' : 'save the row'),
			},
		)

	const onSave = (row: MappingRow, valueLabel?: string) => {
		if (!editing) return
		const saves = editing.several
			? editing.several.map(({ key, label }) => ({ key, row: { ...row, label }, valueLabel }))
			: [{ key: editing.key, row, valueLabel }]
		saveMany(saves, saves.length > 1 ? `Draft saved: ${saves.length} rows` : 'Draft saved', close)
	}

	const onPublishNow = (row: MappingRow) =>
		quick.mutate(
			{ kind, row },
			{
				onSuccess: (version) => {
					notification.success(`Published version ${version.version}: the fix is re-mapping the bundles it holds`)
					close()
				},
				onError: (cause) => refused(cause, 'publish the row'),
			},
		)

	const onDelete = () => {
		if (!editing) return
		remove.mutate(
			{ kind, key: editing.key, revert: false },
			{
				onSuccess: () => {
					notification.success('Deleted in the draft')
					close()
				},
				onError: (cause) => refused(cause, 'delete the row'),
			},
		)
	}

	const drawer = (context: DrawerContext): ReactNode =>
		editing ? (
			<MappingDrawer
				key={`${editing.key}:${reloads}`}
				open
				kind={kind}
				title={editing.title}
				initial={(editing.key !== NEW_ROW_KEY ? resolve(editing.key) : undefined) ?? editing.row}
				labels={editing.several?.map(({ label }) => label)}
				sourceLabel={editing.sourceLabel}
				context={context}
				errors={errors}
				saving={save.isPending || quick.isPending || remove.isPending}
				canPublishNow={editing.fromFix && !editing.several}
				canDelete={editing.canDelete}
				onSave={onSave}
				onPublishNow={onPublishNow}
				onDelete={onDelete}
				onCancel={close}
			/>
		) : null

	return { editing, open, close, drawer, saveMany, saving: save.isPending }
}

/**
 * Opens the drawer an error's Fix link asks for (`?…&edit=1`), once the tab's rows
 * are loaded, then drops `edit` so a reload does not open it again. The other
 * parameters stay: they are the tab's filters.
 */
export function useFixLink(ready: boolean, openFrom: (params: URLSearchParams) => void) {
	const [params, setParams] = useSearchParams()
	const handled = useRef(false)
	useEffect(() => {
		if (!ready || handled.current || params.get('edit') !== '1') return
		handled.current = true
		openFrom(params)
		const next = new URLSearchParams(params)
		next.delete('edit')
		setParams(next, { replace: true })
	}, [ready, params, openFrom, setParams])
	return params
}
